import { useEffect, useState } from 'react';
import {
  Button, Chip, CircularProgress, InputAdornment, TextField,
  Tooltip, Tab, Tabs, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText
} from '@mui/material';
import { ApiRequest } from 'GlobalFunctions';
import { useSnackbar } from 'notistack';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import * as XLSX from 'xlsx';

// ─── helpers ─────────────────────────────────────────────────────────────────
const planColor = (plan_type: string) => {
  if (plan_type === 'preferente') return { bg: '#fff3cd', color: '#856404', label: 'Preferente' };
  if (plan_type === 'destacado')  return { bg: '#d1ecf1', color: '#0c5460', label: 'Destacado'  };
  return { bg: '#f0f4f8', color: '#6b7280', label: 'Básico' };
};

const estadoChip = (estado: string) => {
  if (estado === 'Activa')    return { bg: '#dcfce7', color: '#15803d', icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} /> };
  if (estado === 'Pendiente') return { bg: '#fef9c3', color: '#92400e', icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} /> };
  return { bg: '#fee2e2', color: '#b91c1c', icon: <CancelOutlinedIcon sx={{ fontSize: 14 }} /> };
};

const SummaryCard = ({ label, value, color }: any) => (
  <div style={{
    background: '#fff', borderRadius: 12, padding: '16px 20px',
    boxShadow: '0 1px 6px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color}`
  }}>
    <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
    <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{label}</div>
  </div>
);

// ─── Nurses tab ──────────────────────────────────────────────────────────────
function NursesTab() {
  const [list, setList]       = useState<any[]>([]);
  const [fetching, setFetching] = useState(true);
  const [search, setSearch]   = useState('');
  const [filterEstado,     setFilterEstado]     = useState('Todas');
  const [filterTipo,       setFilterTipo]       = useState('Todos');
  const [filterPlan,       setFilterPlan]       = useState('Todos');
  const [filterResidencia, setFilterResidencia] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const [notifyOpen, setNotifyOpen]       = useState(false);
  const [notifySending, setNotifySending] = useState(false);
  const [notifySubject, setNotifySubject] = useState('Tu perfil en Especialista en Heridas está incompleto');
  const [notifyBody, setNotifyBody]       = useState(
    'Hola {{nombre}},\n\nSomos del equipo de Especialista en Heridas. Detectamos que tu perfil tiene información faltante: {{faltantes}}.\n\nPor favor inicia sesión en https://especialistaenheridas.cl/login y completa los datos que te solicitamos al entrar a tu cuenta.\n\n¡Muchas gracias por ser parte de nuestra plataforma!'
  );

  const [noRutOpen, setNoRutOpen]       = useState(false);
  const [noRutSending, setNoRutSending] = useState(false);
  const [noRutSubject, setNoRutSubject] = useState('Dato faltante en tu cuenta — Especialista en Heridas');
  const [noRutBody, setNoRutBody]       = useState(
    'Hola {{nombre}},\n\nSomos del equipo de Especialista en Heridas. Notamos que tu cuenta no tiene un RUT registrado, lo cual es obligatorio para operar en la plataforma.\n\nPor favor inicia sesión en https://especialistaenheridas.cl/login y completa los datos que te solicitamos al entrar a tu cuenta.\n\n¡Gracias por ser parte de nuestra plataforma!'
  );

  const sendNoRutNotices = () => {
    setNoRutSending(true);
    ApiRequest({
      url: '/users/notify-no-rut',
      method: 'POST',
      body: { subject: noRutSubject, body: noRutBody },
      setResponse: (res: any) => {
        setNoRutSending(false);
        setNoRutOpen(false);
        if (res.status === 'success') {
          enqueueSnackbar(`Aviso enviado a ${res.sent} de ${res.total_targets} enfermeras sin RUT.`, { variant: 'success' });
        } else {
          enqueueSnackbar(res.message || 'No se pudo enviar el aviso', { variant: 'error' });
        }
      },
      onError: () => {
        setNoRutSending(false);
        setNoRutOpen(false);
        enqueueSnackbar('No se pudo enviar el aviso', { variant: 'error' });
      }
    });
  };

  const sendIncompleteNotices = () => {
    setNotifySending(true);
    ApiRequest({
      url: '/nurses/notify-incomplete',
      method: 'POST',
      body: { subject: notifySubject, body: notifyBody },
      setResponse: (res: any) => {
        setNotifySending(false);
        setNotifyOpen(false);
        if (res.status === 'success') {
          enqueueSnackbar(`Aviso enviado a ${res.sent} de ${res.total_targets} enfermeras con perfil incompleto.`, { variant: 'success' });
        } else {
          enqueueSnackbar(res.message || 'No se pudo enviar el aviso masivo', { variant: 'error' });
        }
      },
      onError: () => {
        setNotifySending(false);
        setNotifyOpen(false);
        enqueueSnackbar('No se pudo enviar el aviso masivo', { variant: 'error' });
      }
    });
  };

  useEffect(() => {
    ApiRequest({
      url: '/nurses/report',
      method: 'GET',
      setResponse: (res: any) => {
        if (res.status === 'success') setList(res.list);
        setFetching(false);
      }
    });
  }, []);

  const filtered = list.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q || `${r.name} ${r.lastname} ${r.email} ${r.region} ${r.home_location} ${r.locations_labels} ${r.skills_labels}`.toLowerCase().includes(q);
    const matchEstado = filterEstado === 'Todas' || r.estado === filterEstado;
    const matchTipo   = filterTipo === 'Todos'   || (filterTipo === 'Especialista' ? r.advanced_wound_care : !r.advanced_wound_care);
    const matchPlan       = filterPlan === 'Todos' || (r.plan_type || 'basico').toLowerCase() === filterPlan.toLowerCase();
    const matchResidencia = !filterResidencia || (r.home_location || '').toLowerCase().includes(filterResidencia.toLowerCase());
    return matchSearch && matchEstado && matchTipo && matchPlan && matchResidencia;
  });

  const exportExcel = () => {
    const rows = filtered.map((r, i) => ({
      '#': i + 1,
      Estado: r.estado || '',
      Nombre: r.name,
      Apellido: r.lastname,
      Email: r.email,
      Teléfono: r.phone,
      WhatsApp: r.whatsapp || '',
      Tipo: r.tipo,
      Región: r.region,
      'Comuna de Residencia': r.home_location || '',
      Comunas: r.locations_labels,
      Procedimientos: r.skills_labels,
      Servicio: r.service || '',
      Precio: r.price || '',
      Plan: r.plan_type || 'básico',
      'Vencimiento Plan': r.plan_expiry_date ? String(r.plan_expiry_date).slice(0, 10) : '',
      'Fecha Registro': r.created_at ? new Date(r.created_at).toLocaleDateString('es-CL') : '',
      'Rating Promedio': r.rating_promedio,
      'N° Evaluaciones': r.rating_count,
      'Reg. Superintendencia': r.sup_salud_registration || '',
      'Emite Boleta': r.issues_boleta ? 'Sí' : 'No',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      {wch:4},{wch:12},{wch:16},{wch:16},{wch:30},{wch:14},{wch:14},{wch:24},
      {wch:22},{wch:20},{wch:32},{wch:40},{wch:20},{wch:12},{wch:12},{wch:16},{wch:10},{wch:12},{wch:24},{wch:12}
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Profesionales');
    XLSX.writeFile(wb, `profesionales_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const cards = [
    { label: 'Total Registradas',       value: list.length,                                            color: '#125095' },
    { label: 'Activas',                 value: list.filter(r => r.estado === 'Activa').length,         color: '#15803d' },
    { label: 'Pendientes Aprobación',   value: list.filter(r => r.estado === 'Pendiente').length,      color: '#d97706' },
    { label: 'Especialistas en Heridas',value: list.filter(r => r.advanced_wound_care).length,         color: '#e05b28' },
    { label: 'Con Plan Pagado',         value: list.filter(r => r.plan_type !== 'basico').length,      color: '#9333ea' },
    { label: 'Perfil Incompleto',       value: list.filter(r => r.profile_incomplete).length,          color: '#ea580c' },
    { label: 'Sin RUT',                 value: list.filter(r => r.missing_rut).length,                  color: '#dc2626' },
  ];

  const thStyle: any = {
    padding: '11px 14px', color: '#fff', fontWeight: 600,
    textAlign: 'left', fontSize: 12, whiteSpace: 'nowrap'
  };

  return (
    <>
      {/* Filters row */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>
        <TextField
          size="small"
          placeholder="Buscar nombre, email, región..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Estado</InputLabel>
          <Select value={filterEstado} label="Estado" onChange={e => setFilterEstado(e.target.value)}>
            <MenuItem value="Todas">Todas</MenuItem>
            <MenuItem value="Activa">Activas</MenuItem>
            <MenuItem value="Pendiente">Pendientes de Aprobación</MenuItem>
            <MenuItem value="Inactiva">Inactivas</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Tipo</InputLabel>
          <Select value={filterTipo} label="Tipo" onChange={e => setFilterTipo(e.target.value)}>
            <MenuItem value="Todos">Todos los tipos</MenuItem>
            <MenuItem value="Especialista">Especialista en Heridas</MenuItem>
            <MenuItem value="General">Enfermera General</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Plan</InputLabel>
          <Select value={filterPlan} label="Plan" onChange={e => setFilterPlan(e.target.value)}>
            <MenuItem value="Todos">Todos los planes</MenuItem>
            <MenuItem value="basico">Básico</MenuItem>
            <MenuItem value="preferente">Preferente</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Filtrar por residencia..."
          value={filterResidencia}
          onChange={e => setFilterResidencia(e.target.value)}
          sx={{ width: 200 }}
        />
        <Button
          variant="contained"
          startIcon={<MarkEmailReadOutlinedIcon />}
          onClick={() => setNoRutOpen(true)}
          disabled={list.filter(r => r.missing_rut).length === 0}
          sx={{ ml: 'auto', background: '#dc2626', '&:hover': { background: '#b91c1c' }, fontWeight: 600 }}
        >
          Avisar Sin RUT ({list.filter(r => r.missing_rut).length})
        </Button>
        <Button
          variant="contained"
          startIcon={<MarkEmailReadOutlinedIcon />}
          onClick={() => setNotifyOpen(true)}
          disabled={list.filter(r => r.profile_incomplete).length === 0}
          sx={{ background: '#ea580c', '&:hover': { background: '#c2410c' }, fontWeight: 600 }}
        >
          Avisar Perfiles Incompletos ({list.filter(r => r.profile_incomplete).length})
        </Button>
        <Button
          variant="contained"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={exportExcel}
          disabled={filtered.length === 0}
          sx={{ background: '#1d6f42', '&:hover': { background: '#155c36' }, fontWeight: 600 }}
        >
          Exportar Excel
        </Button>
      </div>

      {/* Dialog: aviso masivo perfiles incompletos */}
      <Dialog open={notifyOpen} onClose={() => !notifySending && setNotifyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar aviso de perfil incompleto</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Se enviará este correo a las <b>{list.filter(r => r.profile_incomplete).length}</b> enfermeras con perfil incompleto. Puedes editar el texto antes de enviar.
            <br />
            <span style={{ fontSize: 12, color: '#888' }}>
              <code>{'{{nombre}}'}</code>, <code>{'{{faltantes}}'}</code> y <code>{'{{link}}'}</code> se reemplazan automáticamente por los datos de cada enfermera.
            </span>
          </DialogContentText>
          <TextField
            label="Asunto"
            fullWidth size="small"
            value={notifySubject}
            onChange={e => setNotifySubject(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Mensaje"
            fullWidth multiline minRows={7}
            value={notifyBody}
            onChange={e => setNotifyBody(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyOpen(false)} disabled={notifySending}>Cancelar</Button>
          <Button
            onClick={sendIncompleteNotices}
            variant="contained"
            disabled={notifySending}
            sx={{ background: '#ea580c', '&:hover': { background: '#c2410c' } }}
          >
            {notifySending ? 'Enviando...' : 'Enviar aviso'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: aviso enfermeras sin RUT */}
      <Dialog open={noRutOpen} onClose={() => !noRutSending && setNoRutOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Avisar enfermeras sin RUT</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Se enviará este correo a las <b>{list.filter(r => r.missing_rut).length}</b> enfermeras que no tienen RUT registrado.
            <br />
            <span style={{ fontSize: 12, color: '#888' }}>
              <code>{'{{nombre}}'}</code> y <code>{'{{link}}'}</code> se reemplazan automáticamente con los datos de cada enfermera.
            </span>
          </DialogContentText>
          <TextField
            label="Asunto"
            fullWidth size="small"
            value={noRutSubject}
            onChange={e => setNoRutSubject(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Mensaje"
            fullWidth multiline minRows={6}
            value={noRutBody}
            onChange={e => setNoRutBody(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoRutOpen(false)} disabled={noRutSending}>Cancelar</Button>
          <Button
            onClick={sendNoRutNotices}
            variant="contained"
            disabled={noRutSending}
            sx={{ background: '#dc2626', '&:hover': { background: '#b91c1c' } }}
          >
            {noRutSending ? 'Enviando...' : 'Enviar aviso'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: 12, marginBottom: 20 }}>
        {cards.map(c => <SummaryCard key={c.label} {...c} />)}
      </div>

      {/* Count */}
      <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>
        Mostrando <b>{filtered.length}</b> de {list.length} profesionales
      </div>

      {/* Table */}
      {fetching ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><CircularProgress /></div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 12, boxShadow: '0 1px 8px rgba(0,0,0,0.08)', background: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#125095' }}>
                {['#','Estado','Nombre','Contacto','Tipo','Residencia','Región / Comunas Servicio','Procedimientos','Perfil','Plan','Registro','Rating','Boleta'].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>Sin resultados</td></tr>
              )}
              {filtered.map((row, i) => {
                const plan  = planColor(row.plan_type);
                const est   = estadoChip(row.estado || 'Inactiva');
                const bg    = i % 2 === 0 ? '#fff' : '#f7faff';
                return (
                  <tr key={row.user_id}
                    style={{ background: bg, borderBottom: '1px solid #eef1f6' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#edf4ff')}
                    onMouseLeave={e => (e.currentTarget.style.background = bg)}
                  >
                    <td style={{ padding: '10px 14px', color: '#bbb', fontWeight: 600 }}>{i + 1}</td>

                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: est.bg, color: est.color,
                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600
                      }}>
                        {est.icon}{row.estado || 'Inactiva'}
                      </span>
                    </td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600, color: '#1a1a2e' }}>{row.name} {row.lastname}</div>
                      <div style={{ fontSize: 11, color: '#aaa' }}>ID: {row.user_id}</div>
                    </td>

                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ color: '#444' }}>{row.email}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>
                        {row.phone}{row.whatsapp ? ` · WA: ${row.whatsapp}` : ''}
                      </div>
                    </td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <Chip
                        label={row.advanced_wound_care ? 'Especialista' : 'General'}
                        size="small"
                        sx={{
                          background: row.advanced_wound_care ? '#e3f0ff' : '#e8f5e9',
                          color: row.advanced_wound_care ? '#125095' : '#2e7d32',
                          fontWeight: 600, fontSize: 11
                        }}
                      />
                    </td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 13, color: '#475569' }}>
                        {row.home_location || <span style={{ color: '#cbd5e1' }}>—</span>}
                      </span>
                    </td>

                    <td style={{ padding: '10px 14px', maxWidth: 180 }}>
                      <div style={{ fontWeight: 500, color: '#333' }}>{row.region}</div>
                      <Tooltip title={row.locations_labels || ''} placement="top">
                        <div style={{ fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                          {row.locations_labels || '—'}
                        </div>
                      </Tooltip>
                    </td>

                    <td style={{ padding: '10px 14px', maxWidth: 200 }}>
                      <Tooltip title={row.skills_labels || ''} placement="top">
                        <div style={{ fontSize: 12, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                          {row.skills_labels || '—'}
                        </div>
                      </Tooltip>
                    </td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      {row.profile_incomplete ? (
                        <Tooltip title={`Le falta: ${(row.missing_profile_fields || []).join(', ')}`} placement="top">
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            background: '#ffedd5', color: '#c2410c',
                            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'help'
                          }}>
                            <WarningAmberIcon sx={{ fontSize: 13 }} /> Incompleto
                          </span>
                        </Tooltip>
                      ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#dcfce7', color: '#15803d', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 13 }} /> Completo
                        </span>
                      )}
                    </td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <span style={{ background: plan.bg, color: plan.color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                        {row.badge || plan.label}
                      </span>
                      {row.plan_expiry_date && (
                        <div style={{ fontSize: 10, color: '#aaa', marginTop: 3 }}>
                          Vence: {String(row.plan_expiry_date).slice(0, 10)}
                        </div>
                      )}
                    </td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      {row.created_at ? (
                        <span style={{ fontSize: 12, color: '#475569' }}>
                          {new Date(row.created_at).toLocaleDateString('es-CL')}
                        </span>
                      ) : (
                        <span style={{ color: '#cbd5e1' }}>—</span>
                      )}
                    </td>

                    <td style={{ padding: '10px 14px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 700, color: Number(row.rating_promedio) > 0 ? '#f59e0b' : '#ccc', fontSize: 15 }}>
                        {Number(row.rating_promedio) > 0 ? `★ ${row.rating_promedio}` : '—'}
                      </div>
                      {row.rating_count > 0 && <div style={{ fontSize: 10, color: '#aaa' }}>{row.rating_count} eval.</div>}
                    </td>

                    <td style={{ padding: '10px 14px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {row.issues_boleta ? (
                        <span style={{ background: '#dcfce7', color: '#15803d', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>Sí</span>
                      ) : (
                        <span style={{ color: '#cbd5e1', fontSize: 12 }}>No</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── Patients tab ─────────────────────────────────────────────────────────────
function PatientsTab() {
  const [list, setList]         = useState<any[]>([]);
  const [fetching, setFetching] = useState(true);
  const [search, setSearch]     = useState('');
  const [filterEstado, setFilterEstado] = useState('Todos');

  useEffect(() => {
    ApiRequest({
      url: '/users/patients-report',
      method: 'GET',
      setResponse: (res: any) => {
        if (res.status === 'success') setList(res.list);
        setFetching(false);
      }
    });
  }, []);

  const filtered = list.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q || `${r.name} ${r.lastname} ${r.email} ${r.phone}`.toLowerCase().includes(q);
    const matchEstado = filterEstado === 'Todos' || (filterEstado === 'Activo' ? r.is_active : !r.is_active);
    return matchSearch && matchEstado;
  });

  const exportExcel = () => {
    const rows = filtered.map((r, i) => ({
      '#': i + 1,
      Nombre: r.name,
      Apellido: r.lastname,
      Email: r.email,
      Teléfono: r.phone,
      'Conversaciones': r.total_conversations,
      'Evaluaciones Dadas': r.total_ratings_given,
      Activo: r.is_active ? 'Sí' : 'No',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{wch:4},{wch:16},{wch:16},{wch:30},{wch:16},{wch:14},{wch:16},{wch:8}];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pacientes');
    XLSX.writeFile(wb, `pacientes_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const cards = [
    { label: 'Total Pacientes',    value: list.length,                             color: '#125095' },
    { label: 'Activos',            value: list.filter(r => r.is_active).length,    color: '#15803d' },
    { label: 'Con Conversaciones', value: list.filter(r => r.total_conversations > 0).length, color: '#7c3aed' },
    { label: 'Dejaron Evaluación', value: list.filter(r => r.total_ratings_given > 0).length, color: '#d97706' },
  ];

  const thStyle: any = {
    padding: '11px 14px', color: '#fff', fontWeight: 600,
    textAlign: 'left', fontSize: 12, whiteSpace: 'nowrap'
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>
        <TextField
          size="small"
          placeholder="Buscar nombre, email, teléfono..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Estado</InputLabel>
          <Select value={filterEstado} label="Estado" onChange={e => setFilterEstado(e.target.value)}>
            <MenuItem value="Todos">Todos</MenuItem>
            <MenuItem value="Activo">Activos</MenuItem>
            <MenuItem value="Inactivo">Inactivos</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={exportExcel}
          disabled={filtered.length === 0}
          sx={{ ml: 'auto', background: '#1d6f42', '&:hover': { background: '#155c36' }, fontWeight: 600 }}
        >
          Exportar Excel
        </Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: 12, marginBottom: 20 }}>
        {cards.map(c => <SummaryCard key={c.label} {...c} />)}
      </div>

      <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>
        Mostrando <b>{filtered.length}</b> de {list.length} pacientes
      </div>

      {fetching ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><CircularProgress /></div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 12, boxShadow: '0 1px 8px rgba(0,0,0,0.08)', background: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#125095' }}>
                {['#','Nombre','Email','Teléfono','Conversaciones','Evaluaciones Dadas','Estado'].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>Sin resultados</td></tr>
              )}
              {filtered.map((row, i) => {
                const bg = i % 2 === 0 ? '#fff' : '#f7faff';
                return (
                  <tr key={row.user_id}
                    style={{ background: bg, borderBottom: '1px solid #eef1f6' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#edf4ff')}
                    onMouseLeave={e => (e.currentTarget.style.background = bg)}
                  >
                    <td style={{ padding: '10px 14px', color: '#bbb', fontWeight: 600 }}>{i + 1}</td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600, color: '#1a1a2e' }}>{row.name} {row.lastname}</div>
                      <div style={{ fontSize: 11, color: '#aaa' }}>ID: {row.user_id}</div>
                    </td>

                    <td style={{ padding: '10px 14px', color: '#444' }}>{row.email}</td>

                    <td style={{ padding: '10px 14px', color: '#555', whiteSpace: 'nowrap' }}>{row.phone || '—'}</td>

                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <span style={{
                        fontWeight: 700,
                        color: row.total_conversations > 0 ? '#7c3aed' : '#ccc',
                        fontSize: 15
                      }}>
                        {row.total_conversations}
                      </span>
                    </td>

                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <span style={{
                        fontWeight: 700,
                        color: row.total_ratings_given > 0 ? '#d97706' : '#ccc',
                        fontSize: 15
                      }}>
                        {row.total_ratings_given}
                      </span>
                    </td>

                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      {row.is_active
                        ? <CheckCircleOutlineIcon sx={{ color: '#15803d', fontSize: 20 }} />
                        : <CancelOutlinedIcon sx={{ color: '#d32f2f', fontSize: 20 }} />
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function NursesReport(props: any) {
  const { setLoading } = props;
  const [tab, setTab] = useState(0);

  useEffect(() => {
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="content" style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: '0 0 4px 0', fontSize: 22, fontWeight: 700, color: '#125095' }}>
          Gestión de Usuarios
        </h1>
        <span style={{ color: '#888', fontSize: 13 }}>
          Directorio de profesionales registradas y pacientes de la plataforma
        </span>
      </div>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: '1px solid #e5e7eb', marginBottom: 24 }}
        TabIndicatorProps={{ style: { background: '#125095' } }}
      >
        <Tab label="Profesionales (Enfermeras)" sx={{ fontWeight: 600, textTransform: 'none', fontSize: 14 }} />
        <Tab label="Pacientes" sx={{ fontWeight: 600, textTransform: 'none', fontSize: 14 }} />
      </Tabs>

      {tab === 0 && <NursesTab />}
      {tab === 1 && <PatientsTab />}
    </div>
  );
}
